import {
  $applyNodeReplacement,
  $createTextNode,
  EditorConfig,
  LexicalNode,
  LexicalUpdateJSON,
  SerializedTextNode,
  Spread,
  TextNode,
} from 'lexical'
import {
  $dfs,
  addClassNamesToElement,
  removeClassNamesFromElement,
} from '@lexical/utils'
import { TextMatchTransformer } from '@lexical/markdown'

// Matches {cite: ...} tokens, terminated by the first unescaped `}`.
// Mirrors the parser in Utilities.ShowNotesCitations (backend) closely enough
// for a visual highlight — the backend is the source of truth for compiling.
let CITATION_REGEX = /\{cite:(?:\\.|[^}\\])*\}/
let CITATION_REGEX_GLOBAL = /\{cite:(?:\\.|[^}\\])*\}/g

// Extracts every {cite: ...} token found in a markdown string, e.g. a text
// block's stored markdown. Used to list existing citations across blocks
// without needing a live Lexical editor for each one.
export function extractCitationTokens(markdown: string): string[] {
  return markdown.match(CITATION_REGEX_GLOBAL) ?? []
}

// Strips the "{cite:" / "}" wrapper for display in menus, e.g. in the
// "Choose existing" citation list.
export function getCitationBody(token: string): string {
  return token
    .replace(/^\{cite:/, '')
    .replace(/\}$/, '')
    .trim()
}

export type CitationVariant = 0 | 1

export type SerializedCitationNode = Spread<
  { variant: CitationVariant },
  SerializedTextNode
>

// Lexical's node API is class-based (TextNode is a class we must subclass,
// referencing `this` throughout), matching the pattern Lexical itself uses
// for HashtagNode.
/* eslint-disable fp/no-class, fp/no-this */
export class CitationNode extends TextNode {
  __variant: CitationVariant = 0

  static getType(): string {
    return 'citation'
  }

  static clone(node: CitationNode): CitationNode {
    let cloned = new CitationNode(node.__text, node.__key)
    // eslint-disable-next-line fp/no-mutation
    cloned.__variant = node.__variant
    return cloned
  }

  // Alternates by order of appearance in the document, purely so consecutive
  // citations are easy to tell apart at a glance. Has no bearing on the real
  // (server-computed) reference numbering.
  getVariant(): CitationVariant {
    return this.getLatest().__variant
  }

  setVariant(variant: CitationVariant): this {
    let self = this.getWritable()
    // eslint-disable-next-line fp/no-mutation
    self.__variant = variant
    return self
  }

  createDOM(config: EditorConfig): HTMLElement {
    let element = super.createDOM(config)
    addClassNamesToElement(
      element,
      this.__variant === 0 ? config.theme.citationA : config.theme.citationB,
    )
    return element
  }

  updateDOM(prevNode: this, dom: HTMLElement, config: EditorConfig): boolean {
    let updated = super.updateDOM(prevNode, dom, config)
    if (prevNode.__variant !== this.__variant) {
      removeClassNamesFromElement(
        dom,
        config.theme.citationA,
        config.theme.citationB,
      )
      addClassNamesToElement(
        dom,
        this.__variant === 0 ? config.theme.citationA : config.theme.citationB,
      )
    }
    return updated
  }

  exportJSON(): SerializedCitationNode {
    return {
      ...super.exportJSON(),
      variant: this.__variant,
      type: 'citation',
    }
  }

  static importJSON(serializedNode: SerializedCitationNode): CitationNode {
    // eslint-disable-next-line no-use-before-define
    return $createCitationNode(serializedNode.text).updateFromJSON(
      serializedNode,
    )
  }

  updateFromJSON(
    serializedNode: LexicalUpdateJSON<SerializedCitationNode>,
  ): this {
    let node = super.updateFromJSON(serializedNode)
    return node.setVariant(serializedNode.variant ?? 0) as this
  }

  // A citation is a single unit — typing at either edge shouldn't merge into it.
  // Required override from TextNode; the base implementation reads no instance state either.
  // eslint-disable-next-line class-methods-use-this
  canInsertTextBefore(): boolean {
    return false
  }

  // eslint-disable-next-line class-methods-use-this
  isTextEntity(): boolean {
    return true
  }
}
/* eslint-enable fp/no-class, fp/no-this */

export function $createCitationNode(text: string = ''): CitationNode {
  return $applyNodeReplacement(new CitationNode(text))
}

export function $isCitationNode(
  node: LexicalNode | null | undefined,
): node is CitationNode {
  return node instanceof CitationNode
}

/**
 * Keeps every CitationNode's variant (alternating primary/secondary
 * highlight) in sync with whether it sits directly beside another citation
 * (no prose between them, e.g. "{cite: A}{cite: B}"). A citation defaults to
 * secondary; only when it is immediately preceded by another citation node
 * does it flip to the opposite of that neighbor's variant. Purely cosmetic —
 * makes side-by-side citations easy to tell apart — and has no bearing on
 * real reference numbering, which is computed server-side.
 * Run over the whole document on every citation-related transform, since
 * inserting or removing one citation can change whether its neighbors are
 * still adjacent, and those sibling nodes are not otherwise marked dirty.
 */
function $recalculateCitationVariants(): void {
  $dfs()
    .map(({ node }) => node)
    .filter($isCitationNode)
    .reduce((previousVariant, citationNode) => {
      let isAdjacentToPrevious = $isCitationNode(
        citationNode.getPreviousSibling(),
      )
      let variant: CitationVariant = isAdjacentToPrevious
        ? previousVariant === 0
          ? 1
          : 0
        : 0

      if (citationNode.getVariant() !== variant) {
        citationNode.setVariant(variant)
      }
      return variant
    }, 0 as CitationVariant)
}

/**
 * Converts the first {cite: ...} token found in a plain TextNode into a
 * CitationNode. Registered as a node transform on TextNode (not via
 * @lexical/text's registerLexicalTextEntity) because that helper's adjacency
 * heuristic skips a second entity match that starts immediately after a
 * first one with no separator — exactly the "{cite: A}{cite: B}" case.
 * Splitting off only the first match and leaving the remainder as plain text
 * is sufficient: Lexical marks the remainder dirty, so this transform runs
 * again on it and picks up the next match.
 */
export function $citationForwardTransform(node: TextNode): void {
  if (!node.isSimpleText()) return

  let text = node.getTextContent()
  let match = CITATION_REGEX.exec(text)
  if (match === null) return

  let start = match.index
  let end = start + match[0].length

  let splitNodes =
    start === 0 ? node.splitText(end) : node.splitText(start, end)
  let targetNode = start === 0 ? splitNodes[0] : splitNodes[1]

  let citationNode = $createCitationNode(targetNode.getTextContent())
  citationNode.setFormat(targetNode.getFormat())
  targetNode.replace(citationNode)

  $recalculateCitationVariants()
}

/**
 * Reverts a CitationNode back to plain text if an edit (e.g. deleting the
 * closing brace) made it no longer match the token shape.
 */
export function $citationReverseTransform(node: CitationNode): void {
  let text = node.getTextContent()
  let match = CITATION_REGEX.exec(text)
  if (match === null || match.index !== 0 || match[0].length !== text.length) {
    let textNode = $createTextNode(text)
    textNode.setFormat(node.getFormat())
    node.replace(textNode)
    $recalculateCitationVariants()
    return
  }

  $recalculateCitationVariants()
}

// Lexical's default markdown export escapes backslashes on any TextNode
// (\ -> \\), which would corrupt the citation's own \} escape syntax on
// every save. Export the token's text content verbatim instead.
export let CITATION_TRANSFORMER: TextMatchTransformer = {
  dependencies: [CitationNode],
  export: (node) => {
    if (!$isCitationNode(node)) return null
    return node.getTextContent()
  },
  importRegExp: CITATION_REGEX,
  regExp: CITATION_REGEX,
  replace: (textNode, match) => {
    let citationNode = $createCitationNode(match[0])
    textNode.replace(citationNode)
    $recalculateCitationVariants()
  },
  trigger: '}',
  type: 'text-match',
}
